import { ChangeEvent, FC, useMemo, useState } from 'react';
// Redux
import { useAppSelector } from 'hooks/useStore';
import { selectMedia } from 'store/media/mediaSelectors';
import MediaFormats from 'types/MediaFormats';
// Components
import { Loader, Message } from 'components/Utilities';
import { Select } from 'components/Controls';
import MediaItem from './MediaItem';
// i18next
import { useTranslation } from 'react-i18next';
// Utilities
import { mediasInfo } from 'utilities/utilities';

const MediaList:FC = () => {
  const { t } = useTranslation();

  const [format, setFormat] = useState<MediaFormats | string>('all');
  const handleFormat = (e:ChangeEvent<HTMLSelectElement>) => setFormat(e.target.value);
  
  const media = useAppSelector(selectMedia);

  const videos = media.filter(m => m.type === 'video');
  const audio = media.filter(m => m.type === 'audio');

  const images = useMemo(() => {
    return media.filter(media => {
      const formatFilter = format === 'all' ? true : media.format === format;
      const typeFilter = media.type === 'image';
      return formatFilter && typeFilter;
    });
  }, [media, format]);


  const formatOptions = [
    { value: 'all', label: t('labels.all') },
    ...(Object.keys(MediaFormats) as Array<keyof typeof MediaFormats>)
    .map((key) => ({ value: MediaFormats[key], label: mediasInfo[MediaFormats[key]].label }))
  ];

  if ( !media ) return <Loader />;
  if ( media.length === 0 ) return <Message text={t('pages.media.noData')} />;
  return (
    <div className="flex-1 overflow-y-scroll">
      <div className="flex items-center gap-8">
        <h2
          className="text-md font-bold text-white leading-5 mb-4"
        >{t('labels.images')}</h2>
        <Select
          id="media-format" name="media-format"
          options={formatOptions}
          value={format}
          onChange={handleFormat}
        />
      </div>
      {!images.length && <Message text="No image(s)" />}
      <div className="flex flex-wrap gap-4 pb-4">
        {images.map(image => (
          <MediaItem key={image._id} media={image} />
        ))}
      </div>

      <h2
        className="text-md font-bold text-white leading-5 mb-4"
      >{t('labels.videos')}</h2>
      {!videos.length && <Message text="No video(s)" />}
      <div className="flex flex-wrap gap-4 pb-4">
        {videos.map(video => (
          <MediaItem key={video._id} media={video} />
        ))}
      </div>

      <h2
        className="text-md font-bold text-white leading-5 mb-4"
      >{t('labels.audio')}</h2>
      {!audio.length && <Message text="No audio" />}
      <div className="flex flex-wrap gap-4 pb-4">
        {audio.map(a => (
          <MediaItem key={a._id} media={a} />
        ))}
      </div>
    </div>
  )
}

export default MediaList;
